<template>
  <div class="cash-flow-widget-container">
    <div v-if="loading" class="cash-flow-widget-loading">Loading...</div>
    <div v-else-if="error" class="cash-flow-widget-error">{{ error }}</div>
    <div v-else class="cash-flow-widget-content">
      <div class="cash-flow-widget-header">
        <div class="cash-flow-widget-header-label">Net Cash Flow</div>
        <div 
          class="cash-flow-widget-header-value" 
          :class="netCashFlow >= 0 ? 'cash-flow-widget-positive' : 'cash-flow-widget-negative'"
        >
          {{ formatCurrency(netCashFlow) }}
        </div>
      </div>
      <div class="cash-flow-widget-details">
        <div class="cash-flow-widget-detail-row">
          <div class="cash-flow-widget-detail-label">Money Coming In</div>
          <div class="cash-flow-widget-detail-value cash-flow-widget-positive">{{ formatCurrency(moneyIn) }}</div>
        </div>
        <div class="cash-flow-widget-detail-row">
          <div class="cash-flow-widget-detail-label">Money Going Out</div>
          <div class="cash-flow-widget-detail-value cash-flow-widget-negative">{{ formatCurrency(moneyOut) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './styles/CashFlowWidget.css';
import ApiService from '@/services/api.service';
import logger from '@/utils/logger';

export default {
  name: 'CashFlowWidget',
  props: {
    companyId: { type: [String, Number], required: true }
  },
  data() {
    return {
      moneyIn: 0,
      moneyOut: 0,
      loading: true,
      error: null
    };
  },
  computed: {
    netCashFlow() {
      return this.moneyIn - this.moneyOut;
    }
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        // Fetch invoices to calculate money coming in (amount received)
        const invoices = await ApiService.getAllCustomerInvoices(this.companyId);
        const invoicesArray = Array.isArray(invoices) ? invoices : [];
        
        // Filter out draft and cancelled invoices
        const validInvoices = invoicesArray.filter(inv => 
          inv.status !== 'draft' && inv.status !== 'cancelled'
        );
        
        // Calculate amount received (total - balance)
        this.moneyIn = validInvoices.reduce((sum, inv) => {
          const totalAmount = parseFloat(inv.totalAmount || 0);
          const balanceAmount = parseFloat(inv.balanceAmount || inv.balanceDue || 0);
          return sum + (totalAmount - balanceAmount);
        }, 0);
        
        // Fetch expenses to calculate money going out
        const expenses = await ApiService.getBillsAndExpenses(this.companyId, { view: 'Expenses' });
        const expensesArray = Array.isArray(expenses) ? expenses : [];
        
        // Filter to only include expenses (not bills) and exclude voided/cancelled
        const validExpenses = expensesArray.filter(expense => {
          return expense.type === 'Expense' &&
                 !expense.voided &&
                 expense.status !== 'voided' &&
                 expense.status !== 'cancelled';
        });
        
        // Sum all expense amounts
        this.moneyOut = validExpenses.reduce((sum, expense) => {
          return sum + parseFloat(expense.originalAmount || expense.totalAmount || 0);
        }, 0);
      } catch (error) {
        logger.error('Error fetching cash flow data:', error);
        this.error = 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
  }
};
</script>

